import { ko } from 'date-fns/locale';
import { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { SuperModal } from 'src/components';
import { Label } from 'src/components/common';
import { Button } from 'src/components/common/Button';
import { Time } from 'src/components/common/Time';
import {
  teacherPointLogDelete,
  teacherPointLogUpdateAwardedAt,
  useTeacherPointLogGetOne,
  useTeacherUserGetOne,
  useUserMe,
} from 'src/generated/endpoint';
import { numberWithSign } from 'src/util/string';
import { useModals } from './ModalStack';

export interface PointLogModalProps {
  pointLogId: number;
}

export function PointLogModal({ pointLogId }: PointLogModalProps) {
  const { popModal } = useModals();
  const { t } = useTranslation();
  const { t: tm } = useTranslation('modal', { keyPrefix: 'point_log_modal' });
  const { data: me } = useUserMe();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const { data: pointLog } = useTeacherPointLogGetOne(pointLogId);
  const { data: teacher } = useTeacherUserGetOne(
    pointLog?.teacherId ?? 0,
    {},
    { query: { enabled: !!pointLog?.teacherId } },
  );
  const { data: student } = useTeacherUserGetOne(
    pointLog?.studentId ?? 0,
    {},
    { query: { enabled: !!pointLog?.studentId } },
  );

  async function deletePointLog() {
    if (!confirm(`기록을 삭제할까요?`)) return;
    await teacherPointLogDelete(pointLogId);
    popModal();
  }

  // 날짜를 변경하는 함수
  const handleDateChange = (date: Date) => {
    // 시간을 00:00:00으로 설정
    const resetDate = new Date(date);
    resetDate.setHours(0, 0, 0, 0);
    
    setSelectedDate(resetDate);
    setIsDatePickerOpen(false);
    updatePointLogDate(resetDate);
  };

  // 날짜 업데이트 API 호출 함수
  const updatePointLogDate = async (date: Date) => {
    await teacherPointLogUpdateAwardedAt(pointLogId, { awardedAt: date.toISOString() });
  };

  return (
    <SuperModal
      modalOpen
      setModalClose={popModal}
      className="flex w-96 flex-col gap-4 px-4 py-20 max-md:h-screen-3.5 max-md:w-full max-md:self-start max-md:rounded-none md:max-h-screen-48"
    >
      <h2 className="text-18">{tm('point_log')}</h2>
      <div>
        {me?.id === pointLog?.teacherId && (
          <Button children={t('delete')} onClick={deletePointLog} className="outlined-gray" />
        )}
      </div>
      <Label.col>
        <Label.Text children={tm('point_log_created_at')} />
        <div className="flex items-center">
          <p className="mr-2">
            <Time date={pointLog?.awardedAt || pointLog?.createdAt} className="text-16" format="yyyy.MM.dd" />
          </p>
          {me?.id === pointLog?.teacherId && (
            <div className="relative">
              <Button 
                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)} 
                className="outlined-gray text-sm py-1 px-2"
                children={t('change')} 
              />
              {isDatePickerOpen && (
                <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded shadow-lg">
                  <DatePicker
                    selected={selectedDate || (pointLog?.awardedAt ? new Date(pointLog.awardedAt) : pointLog?.createdAt ? new Date(pointLog.createdAt) : new Date())}
                    onChange={handleDateChange}
                    inline
                    locale={ko}
                    dateFormat="yyyy.MM.dd"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </Label.col>
      <Label.col>
        <Label.Text children={tm('point_log_assigner')} />
        <p>{teacher?.name}</p>
      </Label.col>
      <Label.col>
        <Label.Text children={tm('point_log_assignee')} />
        <p>{student?.name}</p>
      </Label.col>
      <Label.col>
        <Label.Text children={tm('point_log_title')} />
        <p>{pointLog?.title}</p>
      </Label.col>
      <Label.col>
        <Label.Text children={tm('point_log_value')} />
        <p>{numberWithSign(pointLog?.value)}</p>
      </Label.col>
    </SuperModal>
  );
}
